import { createElement, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { DownloadDarkIcon, EyeBlueIcon, UploadBlueIcon } from "../../../assets";
import { InteractionObjectType, InteractionType, OrganisationTypeConstants } from "../../../constants";
import { InteractionNoteData, PillData, SelectData } from "../../../models";
import { IStateOrigin } from "../../../navigators/navigateStateObjects";
import { downloadDealDocument, updateDeal, UpdateDealRequest } from "../../../service/fund";
import {
  getDeal,
  GetDealResponse,
  searchDealDocuments,
  SearchDealDocumentsRequest,
  SearchDealDocumentsResponse,
  searchInteractions,
  searchOrganisationUsers,
} from "../../../service/query";
import { getTaskBoard } from "../../../service/query/QueryService.full";
import { ResultData, ServiceError, Status } from "../../../service/Shared";
import { getErrorMessageFromCode } from "../../../service/ValidationErrorFormatter";
import { InteractionNote } from "../../../templates/NotesPanel/models";
import { TagsContext } from "../../../templates/TagsPanel/TagsContext";
import { useAuth } from "../../../useAuth";
import type { WorkflowTemplatePhaseItem } from "../../../utils";
import {
  ERROR_APPLICATION,
  flattenObject,
  initiateDocumentDownloadViaBrowser,
  jsonReviver,
  normaliseWorkflowTemplatePhases,
  parseDateOnlyStringYYYYMMDD,
  parsePercentageInputNullable,
} from "../../../utils";
import {
  getCollaboratorRoute,
  getDealDocumentDetailsRoute,
  getLandholdingDashboardRoute,
  getPipelineTrackerRoute,
  getProjectDetailsByUuid,
  getWorkflowTasksBoardRoute,
} from "../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridIconCellFormatterData,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
} from "../../../widget";
import { CloseDealStatus } from "./components/CloseDealModal";

export interface UseDealReturnData {
  deal: GetDealResponse | undefined;
  dealWorkflowUuid: string | undefined;
  workflowTemplatePhases: WorkflowTemplatePhaseItem[];
  isLoading: boolean;
  isEditing: boolean;
  isSaving: boolean;
  validationErrors: ServiceError[];
  dealSummaryForm: DealSummaryFormState;
  dealSourceOptions: SelectData;
  organisationUsersSelectData: SelectData;
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: Dispatch<SetStateAction<boolean>>;
  showSelectWorkflowTemplateModal: boolean;
  setShowSelectWorkflowTemplateModal: Dispatch<SetStateAction<boolean>>;
  showEditDealDatesPanel: boolean;
  setShowEditDealDatesPanel: Dispatch<SetStateAction<boolean>>;
  showCloseDealModal: boolean;
  openCloseDealModal: () => void;
  closeCloseDealModal: () => void;
  applyCloseDealUpdate: (status: CloseDealStatus, rowVersion?: number) => void;
  refreshDeal: () => Promise<void>;
  notePreviewItems: InteractionNote[];
  refreshNotes: () => void;
  tags: PillData[];
  showTagsPanel: boolean;
  setShowTagsPanel: (value: boolean) => void;
  refreshTags: () => void;
  startEditing: () => void;
  cancelEditing: () => void;
  saveDealSummary: () => Promise<void>;
  setDealSummaryFormValue: <K extends keyof DealSummaryFormState>(key: K, value: DealSummaryFormState[K]) => void;
  handleBackClick: () => void;
  dealStructureColumns: DataGridColumnDefinition[];
  dealStructureData: DealStructureGridRow[];
  onChangeDealStructure: DataGridCursorDataLoadEventHandler;
  dealDocumentsColumns: DataGridColumnDefinition[];
  dealDocumentsLoading: boolean;
  onDealDocGridChange: DataGridCursorDataLoadEventHandler;
  dealDocumentsRefreshToken: boolean;
  setDealDocumentsRefreshToken: Dispatch<SetStateAction<boolean>>;
  dealUuid?: string;
  showUploadDealDocumentModal: boolean;
  uploadDealDocumentModalDocumentType?: string;
  uploadDealDocumentModalCustomVariant?: string;
  uploadDealDocumentModalDealDocumentUuid?: string;
  openUploadDealDocumentModal: (options?: UploadDealDocumentModalOptions) => void;
  closeUploadDealDocumentModal: () => void;
}

type DealStructureRow = { typeLabel: string; relatedObjectUuid: string };
type DealStructureGridRow = {
  uuid: string;
  structureType: string;
  relatedObjectUuid: string;
  relatedObjectType: string;
  relatedObjectLink: DataGridLinkCellFormatterData | string;
};

type UploadDealDocumentModalOptions = {
  dealDocumentUuid?: string;
  documentType?: string;
  customVariant?: string;
};

type DealSummaryFormState = {
  conversionProbability: string;
  description: string;
  name: string;
  dealLeadUuid: string;
  dealTeamUuids: string[];
  dealSource: string;
  introductionDate: Date | undefined;
  targetCloseDate: Date | undefined;
  investmentValue: string;
  estimatedFees: string;
  targetReturns: string;
  projectedIrr: string;
  projectDpi: string;
  moneyMultiple: string;
};

const dealStructureColumns: DataGridColumnDefinition[] = [
  {
    key: "structureType",
    name: "Structure type",
    dataType: "string",
    sortable: false,
    filterable: false,
  },
  {
    key: "relatedObjectLink",
    name: "Related object",
    dataType: "string",
    formatter: "link",
    sortable: false,
    filterable: false,
  },
];

const appendDealStructureRows = (
  typeLabel: string,
  items: string[] | null | undefined,
  relatedObjectUuids: Set<string>,
  dealStructureRowsData: DealStructureRow[]
): void => {
  items?.forEach((jsonStr) => {
    const item = JSON.parse(jsonStr) as Record<string, unknown>;
    const rawRelatedObjects = item?.relatedObjects;
    let relatedObjectsArray: unknown[];
    if (Array.isArray(rawRelatedObjects)) {
      relatedObjectsArray = rawRelatedObjects;
    } else if (rawRelatedObjects != null) {
      relatedObjectsArray = [rawRelatedObjects];
    } else {
      relatedObjectsArray = [];
    }
    relatedObjectsArray.forEach((relatedObject) => {
      const relatedObjectUuid = (relatedObject as { uuid?: string })?.uuid;
      if (relatedObjectUuid && relatedObjectUuids.has(relatedObjectUuid)) {
        dealStructureRowsData.push({ typeLabel, relatedObjectUuid });
      }
    });
  });
};

const parseNumberInput = (value: string, fallback: number): number => {
  if (!value.trim()) return fallback;
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseNumberInputNullable = (value: string, fallback: number | null): number | null => {
  if (!value.trim()) return fallback ?? null;
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : (fallback ?? null);
};

const parseDateValue = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  const parsedDateOnly = parseDateOnlyStringYYYYMMDD(value);
  if (parsedDateOnly) return parsedDateOnly;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const getDealTeamWithLead = (dealTeamUuids: string[], dealLeadUuid: string): string[] => {
  if (!dealLeadUuid) {
    return dealTeamUuids;
  }

  return Array.from(new Set([...dealTeamUuids, dealLeadUuid]));
};

const buildInitialDealSummaryForm = (deal?: GetDealResponse): DealSummaryFormState => {
  return {
    conversionProbability:
      deal?.conversionProbability !== null && deal?.conversionProbability !== undefined
        ? `${deal.conversionProbability * 100}`
        : "",
    description: deal?.description ?? "",
    name: deal?.name ?? "",
    dealLeadUuid: deal?.leadUser?.uuid ?? "",
    dealTeamUuids: deal?.usersInvolved?.map((user) => user.uuid) ?? [],
    dealSource: deal?.source ?? "",
    introductionDate: parseDateValue(deal?.introductionDate),
    targetCloseDate: parseDateValue(deal?.targetCloseDate),
    investmentValue:
      deal?.investmentValue !== null && deal?.investmentValue !== undefined ? `${deal.investmentValue}` : "",
    estimatedFees: deal?.estimatedFees !== null && deal?.estimatedFees !== undefined ? `${deal.estimatedFees}` : "",
    targetReturns: deal?.targetReturns !== null && deal?.targetReturns !== undefined ? `${deal.targetReturns}` : "",
    projectedIrr: deal?.projectIrr !== null && deal?.projectIrr !== undefined ? `${deal.projectIrr}` : "",
    projectDpi: deal?.projectedDpi !== null && deal?.projectedDpi !== undefined ? `${deal.projectedDpi}` : "",
    moneyMultiple: deal?.moneyMultiple !== null && deal?.moneyMultiple !== undefined ? `${deal.moneyMultiple}` : "",
  };
};

export const useDeal = (): UseDealReturnData => {
  const location = useLocation();
  const { dealUuid } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { currentOrganisationUuid } = useAuth();
  const { currentTags, setObjectUuid, refresh, setRefresh } = useContext(TagsContext);
  const stateOrigin = state?.origin as IStateOrigin | undefined;
  const [deal, setDeal] = useState<GetDealResponse>();
  const [dealWorkflowUuid, setDealWorkflowUuid] = useState<string>();
  const [workflowTemplatePhases, setWorkflowTemplatePhases] = useState<WorkflowTemplatePhaseItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ServiceError[]>([]);
  const [dealSummaryForm, setDealSummaryForm] = useState<DealSummaryFormState>(buildInitialDealSummaryForm());
  const [dealStructureData, setDealStructureData] = useState<DealStructureGridRow[]>([]);
  const [dealDocumentsLoading, setDealDocumentsLoading] = useState<boolean>(false);
  const [dealDocumentsRefreshToken, setDealDocumentsRefreshToken] = useState<boolean>(false);
  const [notePreviewItems, setNotePreviewItems] = useState<InteractionNote[]>([]);
  const [showTagsPanel, setShowTagsPanel] = useState<boolean>(false);
  const [refreshNotesToggle, setRefreshNotesToggle] = useState<boolean>(false);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState<boolean>(false);
  const [showSelectWorkflowTemplateModal, setShowSelectWorkflowTemplateModal] = useState<boolean>(false);
  const [showEditDealDatesPanel, setShowEditDealDatesPanel] = useState<boolean>(false);
  const [showCloseDealModal, setShowCloseDealModal] = useState<boolean>(false);
  const [showUploadDealDocumentModal, setShowUploadDealDocumentModal] = useState<boolean>(false);
  const [organisationUsersSelectData, setOrganisationUsersSelectData] = useState<SelectData>([]);
  const [organisationUsersByUuid, setOrganisationUsersByUuid] = useState<
    Record<string, { email: string; fullName: string; role: string }>
  >({});
  const [uploadDealDocumentModalDocumentType, setUploadDealDocumentModalDocumentType] = useState<string | undefined>();
  const [uploadDealDocumentModalCustomVariant, setUploadDealDocumentModalCustomVariant] = useState<
    string | undefined
  >();
  const [uploadDealDocumentModalDealDocumentUuid, setUploadDealDocumentModalDealDocumentUuid] = useState<
    string | undefined
  >();
  const dealSourceOptions = useMemo<SelectData>(
    () => [
      { key: "Supplier led", value: "Supplier led" },
      { key: "Buyer led", value: "Buyer led" },
      { key: "Internal", value: "Internal" },
      { key: "Directory", value: "Directory" },
    ],
    []
  );
  const onChangeDealStructure: DataGridCursorDataLoadEventHandler = async ({ paging }) => {
    return {
      resultData: dealStructureData,
      paging: {
        pageSize: paging.pageSize,
        totalCount: dealStructureData.length,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  };

  const dealDocumentsColumns: DataGridColumnDefinition[] = useMemo(
    () => [
      {
        key: "documentType",
        name: "Type",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 220,
        filterable: true,
        sortable: true,
      },
      {
        key: "customVariant",
        name: "Variant",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 200,
        filterable: true,
        sortable: true,
      },
      {
        key: "latestVersion",
        name: "Version",
        dataType: "number",
        formatter: "align",
        minWidth: 110,
        alignment: "center",
        filterable: false,
        sortable: false,
      },
      {
        key: "lastUpdated",
        name: "Last updated",
        dataType: "Date",
        formatter: "dateOnly",
        minWidth: 140,
        alignment: "center",
        filterable: false,
        sortable: false,
      },
      {
        key: "createdByUser.fullName",
        name: "Last updated by",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 160,
        filterable: true,
        sortable: true,
      },
      {
        key: "detailsIcon",
        name: "Details",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        minWidth: 90,
        filterable: false,
        sortable: false,
      },
      {
        key: "downloadIcon",
        name: "Download latest",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        minWidth: 140,
        filterable: false,
        sortable: false,
      },
      {
        key: "uploadIcon",
        name: "Upload new version",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        minWidth: 160,
        filterable: false,
        sortable: false,
      },
    ],
    []
  );

  const mapDealDocumentSortKey = (key: string): NonNullable<SearchDealDocumentsRequest["sort"]>[number]["key"] => {
    switch (key) {
      case "documentType":
        return "results.documentType";
      case "customVariant":
        return "results.customVariant";
      case "createdByUser.fullName":
        return "results.createdByUser.fullName";
      case "createdAt":
        return "results.createdAt";
      default:
        return "results.createdAt";
    }
  };

  const onDealDocGridChange: DataGridCursorDataLoadEventHandler = async ({ filtering, paging, sorting }) => {
    if (!dealUuid) {
      return {
        resultData: [],
        paging: {
          pageSize: paging.pageSize,
          totalCount: 0,
          startCursor: "",
          endCursor: "",
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    setDealDocumentsLoading(true);

    try {
      const response = await searchDealDocuments({
        dealUuid,
        paging: {
          afterCursor: paging.afterCursor ?? null,
          beforeCursor: paging.beforeCursor ?? null,
          limit: paging.pageSize,
        },
        sort:
          sorting.length > 0
            ? sorting.map((sortItem) => ({
                key: mapDealDocumentSortKey(sortItem.key as string),
                direction: sortItem.direction,
              }))
            : [
                {
                  key: "results.createdAt",
                  direction: "desc",
                },
              ],
        filter: { results: dataGridMapFilterCriteria(filtering) },
      });

      const resultData: ResultData[] =
        response.data?.results?.map((document: SearchDealDocumentsResponse["results"][number]) => {
          const row = flattenObject(document) as ResultData;
          row.documentType = document.documentType || "--";
          row.customVariant = document.customVariant || "--";
          row.latestVersion = document.latestVersion ?? 0;
          row.lastUpdated = document.createdAt ? new Date(document.createdAt) : null;
          row["createdByUser.fullName"] = document.createdByUser?.fullName ?? document.createdByUser?.email ?? "--";
          row.detailsIcon = {
            action: () => {
              const documentUuid = document.uuid ?? document.file?.uuid;
              if (!documentUuid) return;
              if (!dealUuid) return;
              navigate(getDealDocumentDetailsRoute(dealUuid, documentUuid, OrganisationTypeConstants.ASSET_MANAGER), {
                state: {
                  documentType: document.documentType || "",
                  customVariant: document.customVariant || "",
                  from: `${location.pathname}${location.search}`,
                },
              });
            },
            icon: createElement(EyeBlueIcon, { width: 20, height: 20 }),
          } as DataGridIconCellFormatterData;
          row.downloadIcon = {
            action: () => {
              if (!document.versionUuid) return;
              downloadDealDocument({ dealDocumentHistoryUuid: document.versionUuid }).then((res: unknown) => {
                initiateDocumentDownloadViaBrowser(res);
              });
            },
            icon: createElement(DownloadDarkIcon, { width: 20, height: 20 }),
          } as DataGridIconCellFormatterData;
          row.uploadIcon = {
            action: () => {
              const documentUuid = document.uuid ?? document.file?.uuid;
              if (!documentUuid) return;
              setUploadDealDocumentModalDealDocumentUuid(documentUuid);
              setUploadDealDocumentModalDocumentType(document.documentType || "");
              setUploadDealDocumentModalCustomVariant(document.customVariant || "");
              setShowUploadDealDocumentModal(true);
            },
            icon: createElement(UploadBlueIcon, { width: 20, height: 20 }),
          } as DataGridIconCellFormatterData;
          row.uuid = document.uuid ?? document.file?.uuid ?? `${document.documentType}-${document.latestVersion}`;
          return row;
        }) ?? [];

      return {
        resultData,
        paging: {
          pageSize: paging.pageSize,
          totalCount: response.data?.paging?.total ?? 0,
          startCursor: response.data?.paging?.startCursor ?? "",
          endCursor: response.data?.paging?.endCursor ?? "",
          hasNextPage: response.data?.paging?.hasNextPage ?? false,
          hasPreviousPage: response.data?.paging?.hasPreviousPage ?? false,
        },
      };
    } finally {
      setDealDocumentsLoading(false);
    }
  };

  const refreshTags = useCallback((): void => {
    setRefresh(!refresh);
  }, [refresh, setRefresh]);

  const refreshNotes = useCallback((): void => {
    setRefreshNotesToggle((prev) => !prev);
  }, []);

  const loadDealPageData = useCallback(async (): Promise<void> => {
    if (!dealUuid) {
      setIsLoading(false);
      setDealWorkflowUuid(undefined);
      setWorkflowTemplatePhases([]);
      return;
    }

    setIsLoading(true);

    try {
      const [dealResponse, taskBoardResponse] = await Promise.all([
        getDeal({ dealUuid }),
        getTaskBoard({
          showComplete: true,
          paging: {
            afterCursor: null,
            beforeCursor: null,
            limit: 1,
          },
          filter: { results: { dealUuid: { operator: "eq", value: dealUuid } } },
        }),
      ]);

      if (dealResponse.status === Status.Success && dealResponse.data) {
        const responseDeal = dealResponse.data;
        setDeal(responseDeal);
        if (!isEditing) {
          setDealSummaryForm(buildInitialDealSummaryForm(responseDeal));
        }

        const relatedObjects = responseDeal.relatedObjects ?? [];
        const relatedObjectUuids = new Set<string>(
          relatedObjects.map((object) => object.objectUuid).filter((uuid): uuid is string => Boolean(uuid))
        );

        const dealStructureRowsData: DealStructureRow[] = [];

        appendDealStructureRows(
          "Equity",
          responseDeal.dealSpecificData?.equity,
          relatedObjectUuids,
          dealStructureRowsData
        );
        appendDealStructureRows(
          "Land acquisition",
          responseDeal.dealSpecificData?.landAcquisition,
          relatedObjectUuids,
          dealStructureRowsData
        );
        appendDealStructureRows(
          "Revenue share",
          responseDeal.dealSpecificData?.revenueShare,
          relatedObjectUuids,
          dealStructureRowsData
        );
        appendDealStructureRows(
          "Unit purchase",
          responseDeal.dealSpecificData?.unitPurchase,
          relatedObjectUuids,
          dealStructureRowsData
        );
        appendDealStructureRows("Loan", responseDeal.dealSpecificData?.loan, relatedObjectUuids, dealStructureRowsData);

        const dealStructureGridData: DealStructureGridRow[] = dealStructureRowsData.map((row) => {
          const relatedObject = relatedObjects.find((object) => object.objectUuid === row.relatedObjectUuid);
          const relatedObjectType = relatedObject?.objectType ?? "Unknown";
          const projectName = relatedObject?.project?.displayName ?? "Unknown project";
          const landholdingName = relatedObject?.landholding?.displayName ?? "Unknown landholding";
          const collaboratorName = relatedObject?.collaborator?.name ?? "Unknown collaborator";
          let relatedObjectLink: DataGridLinkCellFormatterData | string = row.relatedObjectUuid;

          if (relatedObjectType === "Project")
            relatedObjectLink = {
              to: getProjectDetailsByUuid(row.relatedObjectUuid, OrganisationTypeConstants.ASSET_MANAGER),
              text: `${projectName}`,
              target: "_self",
              state: { origin: { goBackText: "Back to deal summary", from: location.pathname } as IStateOrigin },
            } as DataGridLinkCellFormatterData;

          if (relatedObjectType === "Landholding")
            relatedObjectLink = {
              to: getLandholdingDashboardRoute(row.relatedObjectUuid, OrganisationTypeConstants.ASSET_MANAGER),
              text: `${landholdingName}`,
              target: "_self",
              state: { origin: { goBackText: "Back to deal summary", from: location.pathname } as IStateOrigin },
            } as DataGridLinkCellFormatterData;
          if (relatedObjectType === "Collaborator")
            relatedObjectLink = {
              to: getCollaboratorRoute(row.relatedObjectUuid, OrganisationTypeConstants.ASSET_MANAGER),
              text: `${collaboratorName}`,
              target: "_self",
              state: { origin: { goBackText: "Back to deal summary", from: location.pathname } as IStateOrigin },
            } as DataGridLinkCellFormatterData;
          return {
            uuid: `${row.typeLabel}-${row.relatedObjectUuid}`,
            structureType: row.typeLabel,
            relatedObjectUuid: row.relatedObjectUuid,
            relatedObjectType,
            relatedObjectLink,
          };
        });

        setDealStructureData(dealStructureGridData);
      }

      if (taskBoardResponse.status === Status.Success && taskBoardResponse.data?.results.length) {
        setDealWorkflowUuid(taskBoardResponse.data.results[0].uuid);
        setWorkflowTemplatePhases(
          normaliseWorkflowTemplatePhases(
            taskBoardResponse.data.results[0].phases?.map((phaseItem) => ({
              keyDates: phaseItem.keyDates ?? [],
              phase: phaseItem.phase,
              sequence: phaseItem.sequence,
            }))
          )
        );
      } else {
        setDealWorkflowUuid(undefined);
        setWorkflowTemplatePhases([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [dealUuid, isEditing, location.pathname]);

  const refreshDeal = useCallback(async (): Promise<void> => {
    await loadDealPageData();
  }, [loadDealPageData]);

  const openCloseDealModal = useCallback((): void => {
    setShowCloseDealModal(true);
  }, []);

  const closeCloseDealModal = useCallback((): void => {
    setShowCloseDealModal(false);
  }, []);

  const applyCloseDealUpdate = useCallback((status: CloseDealStatus, rowVersion?: number): void => {
    setDeal((prevDeal) =>
      prevDeal
        ? {
            ...prevDeal,
            status,
            rowVersion: rowVersion ?? prevDeal.rowVersion,
          }
        : prevDeal
    );
  }, []);

  const openUploadDealDocumentModal = useCallback((options?: UploadDealDocumentModalOptions): void => {
    setUploadDealDocumentModalDealDocumentUuid(options?.dealDocumentUuid);
    setUploadDealDocumentModalDocumentType(options?.documentType);
    setUploadDealDocumentModalCustomVariant(options?.customVariant);
    setShowUploadDealDocumentModal(true);
  }, []);

  const closeUploadDealDocumentModal = useCallback((): void => {
    setShowUploadDealDocumentModal(false);
    setUploadDealDocumentModalDealDocumentUuid(undefined);
    setUploadDealDocumentModalDocumentType(undefined);
    setUploadDealDocumentModalCustomVariant(undefined);
  }, []);

  const quickActionsMenuOptionsList = useMemo<ProfileMenuItemType[]>(() => {
    const baseOptions = dealWorkflowUuid
      ? [
          {
            id: 1,
            value: "See task board",
            action: () =>
              navigate(
                getWorkflowTasksBoardRoute(dealWorkflowUuid, OrganisationTypeConstants.ASSET_MANAGER, {
                  origin: "PipelineTrackerPage",
                }),
                {
                  state: {
                    origin: {
                      goBackText: "Back to deal",
                      from: location.pathname,
                    } as IStateOrigin,
                  },
                }
              ),
          },
        ]
      : [];

    const canCloseDeal = !!deal && !["Approved", "Rejected", "Cancelled"].includes(deal.status ?? "");
    if (canCloseDeal) {
      if (dealWorkflowUuid) {
        baseOptions.push(
          {
            id: 2,
            value: "Edit stage dates",
            action: () => setShowEditDealDatesPanel(true),
          },
          {
            id: 3,
            value: "Close deal",
            action: openCloseDealModal,
          }
        );
      } else {
        baseOptions.push(
          {
            id: 1,
            value: "Apply workflow template",
            action: () => setShowSelectWorkflowTemplateModal(true),
          },
          {
            id: 2,
            value: "Close deal",
            action: openCloseDealModal,
          }
        );
      }
    }

    return baseOptions;
  }, [deal, dealWorkflowUuid, location.pathname, navigate, openCloseDealModal]);

  const getAndSetOrganisationsUsers = useCallback(async (organisationUuid: string): Promise<void> => {
    const res = await searchOrganisationUsers({
      organisationUuid,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 50,
      },
    });

    if (res.status === Status.Success && res.data) {
      setOrganisationUsersSelectData(
        res.data.results.map((x) => ({ key: x.user.uuid, value: x.user.fullName ?? x.user.email }))
      );
      setOrganisationUsersByUuid(
        res.data.results.reduce<Record<string, { email: string; fullName: string; role: string }>>((acc, row) => {
          acc[row.user.uuid] = {
            email: row.user.email,
            fullName: row.user.fullName ?? row.user.email,
            role: row.role,
          };
          return acc;
        }, {})
      );
    }
  }, []);

  const setDealSummaryFormValue = useCallback(
    <K extends keyof DealSummaryFormState>(key: K, value: DealSummaryFormState[K]): void => {
      setDealSummaryForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const startEditing = useCallback((): void => {
    setValidationErrors([]);
    setIsEditing(true);
  }, []);

  const cancelEditing = useCallback((): void => {
    setDealSummaryForm(buildInitialDealSummaryForm(deal));
    setValidationErrors([]);
    setIsEditing(false);
  }, [deal]);

  const saveDealSummary = useCallback(async (): Promise<void> => {
    if (!deal || !dealUuid) {
      return;
    }

    setIsSaving(true);
    setValidationErrors([]);

    try {
      const request: UpdateDealRequest = {
        conversionProbability: parsePercentageInputNullable(
          dealSummaryForm.conversionProbability,
          deal.conversionProbability
        ),
        dealDates: JSON.parse(deal.dealDates),
        dealSpecificData: deal.dealSpecificData as unknown as UpdateDealRequest["dealSpecificData"],
        dealTeam: getDealTeamWithLead(dealSummaryForm.dealTeamUuids ?? [], dealSummaryForm.dealLeadUuid),
        dealUuid,
        description: dealSummaryForm.description,
        estimatedFees: parseNumberInput(dealSummaryForm.estimatedFees, deal.estimatedFees ?? 0),
        introductionDate: dealSummaryForm.introductionDate ?? null,
        IsTemplateSelected: undefined,
        leadUserUuid: dealSummaryForm.dealLeadUuid || "",
        name: dealSummaryForm.name,
        projectedDpi: parseNumberInputNullable(dealSummaryForm.projectDpi, deal.projectedDpi),
        rowVersion: deal.rowVersion,
        source: dealSummaryForm.dealSource || null,
        stage: deal.stage,
        status: deal.status,
        targetCloseDate: dealSummaryForm.targetCloseDate ?? null,
        relatedObjects:
          deal.relatedObjects?.map((relatedObject) => ({
            objectType: relatedObject.objectType,
            objectUuid: relatedObject.objectUuid,
          })) ?? [],
      };

      const response = await updateDeal(request);
      if (response.status === Status.Success && response.data) {
        const nextRowVersion = response.data?.rowVersion;
        setDeal((prev) =>
          prev
            ? {
                ...prev,
                description: dealSummaryForm.description,
                estimatedFees: parseNumberInput(dealSummaryForm.estimatedFees, prev.estimatedFees ?? 0),
                introductionDate: dealSummaryForm.introductionDate ?? null,
                investmentValue: parseNumberInput(dealSummaryForm.investmentValue, prev.investmentValue ?? 0),
                name: dealSummaryForm.name,
                conversionProbability: parsePercentageInputNullable(
                  dealSummaryForm.conversionProbability,
                  prev.conversionProbability
                ),
                leadUser: {
                  ...prev.leadUser,
                  uuid: dealSummaryForm.dealLeadUuid || prev.leadUser?.uuid || "",
                  email: organisationUsersByUuid[dealSummaryForm.dealLeadUuid]?.email ?? prev.leadUser?.email ?? "",
                  fullName:
                    organisationUsersByUuid[dealSummaryForm.dealLeadUuid]?.fullName ?? prev.leadUser?.fullName ?? "",
                },
                moneyMultiple: parseNumberInputNullable(dealSummaryForm.moneyMultiple, prev.moneyMultiple),
                projectIrr: parseNumberInputNullable(dealSummaryForm.projectedIrr, prev.projectIrr),
                projectedDpi: parseNumberInputNullable(dealSummaryForm.projectDpi, prev.projectedDpi),
                source: dealSummaryForm.dealSource || null,
                targetCloseDate: dealSummaryForm.targetCloseDate ?? prev.targetCloseDate,
                targetReturns: parseNumberInput(dealSummaryForm.targetReturns, prev.targetReturns ?? 0),
                usersInvolved: getDealTeamWithLead(
                  dealSummaryForm.dealTeamUuids ?? [],
                  dealSummaryForm.dealLeadUuid
                ).map((uuid) => ({
                  uuid,
                  email: organisationUsersByUuid[uuid]?.email ?? "",
                  fullName: organisationUsersByUuid[uuid]?.fullName ?? "",
                  role: organisationUsersByUuid[uuid]?.role ?? "",
                })),
                rowVersion: nextRowVersion ?? prev.rowVersion,
              }
            : prev
        );
        setIsEditing(false);
      }

      if (response.status === Status.Error) {
        setValidationErrors(
          response.errors ?? [
            {
              code: ERROR_APPLICATION,
              message: getErrorMessageFromCode(ERROR_APPLICATION),
            },
          ]
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [deal, dealSummaryForm, dealUuid, organisationUsersByUuid]);

  const handleBackClick = useCallback((): void => {
    if (stateOrigin?.from) navigate(stateOrigin.from);
    else navigate(getPipelineTrackerRoute(OrganisationTypeConstants.ASSET_MANAGER));
  }, [navigate, stateOrigin?.from]);

  useEffect(() => {
    if (!currentOrganisationUuid) return;
    getAndSetOrganisationsUsers(currentOrganisationUuid);
  }, [currentOrganisationUuid, getAndSetOrganisationsUsers]);

  useEffect(() => {
    loadDealPageData();
  }, [loadDealPageData]);

  const tags = useMemo<PillData[]>(
    () =>
      (currentTags ?? [])
        .filter((tag) => tag.checked && !tag.deleted)
        .map((tag) => ({
          key: tag.uuid,
          label: tag.data.name,
        })),
    [currentTags]
  );

  useEffect(() => {
    if (deal?.uuid && currentOrganisationUuid) {
      setObjectUuid(deal.uuid);
    } else {
      setObjectUuid(undefined);
    }

    return () => {
      setObjectUuid(undefined);
    };
  }, [deal?.uuid, currentOrganisationUuid, setObjectUuid]);

  useEffect(() => {
    if (!deal?.uuid) return;

    const fetchNotes = async (): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.NOTE,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.DEAL,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: deal.uuid,
      };

      const response = await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 3,
        },
        sort: [
          {
            key: "results.createdAt",
            direction: "desc",
          },
        ],
        filter: { results: filterCriteria },
      });

      if (response.status === Status.Success && response.data?.results) {
        setNotePreviewItems(
          response.data.results.map((note) => ({
            uuid: note.uuid ?? "",
            createdAt: note.createdAt,
            createdByUserFullName: note.createdByUser?.fullName ?? "",
            rowVersion: note.rowVersion ?? 0,
            data: JSON.parse(note.data, jsonReviver) as InteractionNoteData,
          }))
        );
      }
    };

    fetchNotes();
  }, [deal?.uuid, refreshNotesToggle]);

  return {
    deal,
    dealWorkflowUuid,
    workflowTemplatePhases,
    isLoading,
    isEditing,
    isSaving,
    validationErrors,
    dealSummaryForm,
    dealSourceOptions,
    organisationUsersSelectData,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,
    showSelectWorkflowTemplateModal,
    setShowSelectWorkflowTemplateModal,
    showEditDealDatesPanel,
    setShowEditDealDatesPanel,
    showCloseDealModal,
    openCloseDealModal,
    closeCloseDealModal,
    applyCloseDealUpdate,
    refreshDeal,
    notePreviewItems,
    refreshNotes,
    tags,
    showTagsPanel,
    setShowTagsPanel,
    refreshTags,
    startEditing,
    cancelEditing,
    saveDealSummary,
    setDealSummaryFormValue,
    handleBackClick,
    dealStructureColumns,
    dealStructureData,
    onChangeDealStructure,
    dealDocumentsColumns,
    dealDocumentsLoading,
    onDealDocGridChange,
    dealDocumentsRefreshToken,
    setDealDocumentsRefreshToken,
    dealUuid,
    showUploadDealDocumentModal,
    uploadDealDocumentModalDocumentType,
    uploadDealDocumentModalCustomVariant,
    uploadDealDocumentModalDealDocumentUuid,
    openUploadDealDocumentModal,
    closeUploadDealDocumentModal,
  };
};
